import { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams, Link } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { validatePassword } from "@/utils/passwordValidation";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import PasswordResetDialog from "@/components/auth/PasswordResetDialog";
import DateOfBirthField from "@/components/auth/DateOfBirthField";

// Age calculation utility
const calculateAge = (dateOfBirth: string): number => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Storage key for blocked emails
const BLOCKED_EMAILS_KEY = "anyskill_blocked_signups";

// Get blocked emails from localStorage
const getBlockedEmails = (): string[] => {
  try {
    const stored = localStorage.getItem(BLOCKED_EMAILS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Add email to blocked list
const blockEmail = (email: string): void => {
  const blocked = getBlockedEmails();
  const normalizedEmail = email.toLowerCase().trim();
  if (!blocked.includes(normalizedEmail)) {
    blocked.push(normalizedEmail);
    localStorage.setItem(BLOCKED_EMAILS_KEY, JSON.stringify(blocked));
  }
};

// Check if email is blocked
const isEmailBlocked = (email: string): boolean => {
  const blocked = getBlockedEmails();
  return blocked.includes(email.toLowerCase().trim());
};

// Enhanced email validation with a more helpful error message
const loginSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address (e.g., name@example.com)."
  }),
  password: z.string().min(1, {
    message: "Password is required."
  })
});

const signupSchema = z.object({
  firstName: z.string().min(2, {
    message: "First name must be at least 2 characters."
  }),
  lastName: z.string().min(1, {
    message: "Last name is required."
  }),
  email: z.string().email({
    message: "Please enter a valid email address (e.g., name@example.com)."
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters."
  }).refine(password => validatePassword(password).isValid, {
    message: "Password must include uppercase, lowercase, number, and special character."
  }),
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: "You must agree to the Privacy Policy and Terms of Service to create an account."
  }),
  dateOfBirth: z.string().min(1, { message: "Date of birth is required." }),
  school: z.string().optional(),
  phoneNumber: z.string().optional(),
});

const Auth = () => {
  const [searchParams] = useSearchParams();
  // Get the mode from URL parameters, default to login if not specified
  const initialMode = searchParams.get("mode") === "signup" ? false : true;
  const [isLogin, setIsLogin] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [suspendRedirect, setSuspendRedirect] = useState(false);
  const [signupCompleteEmail, setSignupCompleteEmail] = useState<string | null>(null);
  const [signupStep, setSignupStep] = useState(1);
  const navigate = useNavigate();
  const {
    signIn,
    signUp,
    user
  } = useAuth();

  // Clear error when changing modes
  useEffect(() => {
    setAuthError(null);
    setPasswordValidationError(null);
    setIsLogin(searchParams.get("mode") === "signup" ? false : true);

    const shouldOpenReset = searchParams.get("reset") === "1";
    setResetDialogOpen(shouldOpenReset);
    setSuspendRedirect(shouldOpenReset);
  }, [searchParams]);

  // Get prefilled email from URL params
  const prefilledEmail = searchParams.get("email") || "";

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: prefilledEmail,
      password: ""
    }
  });

  // Update login form when prefilled email changes
  useEffect(() => {
    if (prefilledEmail) {
      loginForm.setValue("email", prefilledEmail);
    }
  }, [prefilledEmail, loginForm]);

  const signupForm = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      agreeToTerms: false,
      dateOfBirth: "",
      school: "",
      phoneNumber: "",
    }
  });

  const onGoogleSignIn = async () => {
    setAuthError(null);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      setAuthError("Google sign-in failed. Please try again.");
      toast.error("Google sign-in failed");
      return;
    }
    // Redirected to Google, or session already set — the auth listener takes over
  };

  const onAppleSignIn = async () => {
    setAuthError(null);
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      setAuthError("Apple sign-in failed. Please try again.");
      toast.error("Apple sign-in failed");
      return;
    }
    // Redirected to Apple, or session already set — the auth listener takes over
  };

  const onLoginSubmit = async (values: z.infer<typeof loginSchema>) => {
    setLoading(true);
    setAuthError(null);
    try {
      console.log("Login attempt with:", {
        email: values.email,
        passwordLength: values.password?.length || 0
      });
      await signIn(values.email, values.password);
      toast.success("Signed in successfully!");
      navigate("/");
    } catch (error: any) {
      console.error("Login error:", error);
      const msg: string = error?.message || "";
      if (/invalid login credentials/i.test(msg)) {
        setAuthError("That email and password don't match. If you created your account with Google or Apple, use those buttons above — otherwise try \"Forgot password?\".");
      } else if (/email not confirmed/i.test(msg)) {
        setAuthError("Please confirm your email first — check your inbox for the confirmation link.");
      } else {
        setAuthError(msg || "Failed to sign in. Please try again.");
      }
      toast.error("Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      // Check profiles table for existing user with this email
      // This avoids triggering auth errors like "Email not confirmed"
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .maybeSingle();
      
      if (error) {
        console.log("Error checking email:", error);
        return false;
      }
      
      return !!data;
    } catch {
      return false;
    }
  };

  // Function to create a referral record if user signed up with a referral code
  const createReferralRecord = async (newUserId: string) => {
    const referralCode = localStorage.getItem("referral_code");
    if (!referralCode) return;

    try {
      // Find the referrer by code
      const { data: referrer, error: referrerError } = await supabase
        .from("profiles")
        .select("id")
        .eq("referral_code", referralCode)
        .single();

      if (referrerError || !referrer) {
        console.log("Referral code not found or invalid:", referralCode);
        return;
      }

      // Don't allow self-referral
      if (referrer.id === newUserId) {
        console.log("Self-referral not allowed");
        localStorage.removeItem("referral_code");
        return;
      }

      // Create the referral record
      const { error: createError } = await supabase.from("referrals").insert({
        referrer_id: referrer.id,
        referred_user_id: newUserId,
        referral_code_used: referralCode,
        status: "pending",
      });

      if (createError) {
        console.error("Error creating referral record:", createError);
      } else {
        console.log("Referral record created successfully");
        localStorage.removeItem("referral_code");
      }
    } catch (error) {
      console.error("Error processing referral:", error);
    }
  };

  const onSignupSubmit = async (values: z.infer<typeof signupSchema>) => {
    setLoading(true);
    setAuthError(null);
    setPasswordValidationError(null);

    // Check if email is blocked (previous under-13 attempt)
    if (isEmailBlocked(values.email)) {
      setAuthError("This email cannot be used to create an account. Please contact support if you believe this is an error.");
      setLoading(false);
      return;
    }

    // Age validation - block users under 13
    const age = calculateAge(values.dateOfBirth);
    if (age < 13) {
      blockEmail(values.email);
      setAuthError("You must be at least 13 years old to create an account. This email has been blocked from signing up.");
      toast.error("You must be at least 13 years old to sign up");
      setLoading(false);
      return;
    }

    // Additional password validation
    const passwordCheck = validatePassword(values.password);
    if (!passwordCheck.isValid) {
      setPasswordValidationError(passwordCheck.errorMessage);
      setLoading(false);
      return;
    }

    // Check if email already exists
    const emailExists = await checkEmailExists(values.email);
    if (emailExists) {
      setLoading(false);
      toast.info("This email is already registered. Redirecting to login...");
      // Redirect to login with email prefilled
      navigate(`/auth?mode=login&email=${encodeURIComponent(values.email)}`);
      return;
    }

    try {
      console.log("Signup attempt with:", {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        school: values.school || null,
        phoneNumber: values.phoneNumber || null
      });
      const result = await signUp(values.email, values.password, values.firstName, values.lastName, values.dateOfBirth, values.school || null, values.phoneNumber || null);
      
      // Check if signup was successful
      if (result?.user) {
        // Create referral record if user signed up with a referral code
        await createReferralRecord(result.user.id);
        
        // Prompt the user to confirm their email before continuing
        setSignupCompleteEmail(values.email);
        setSuspendRedirect(true);
      } else {
        // No user returned - something went wrong
        setAuthError("Account creation failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Signup error:", error);

      // More user-friendly error messages
      if (error.message.includes("User already registered")) {
        toast.info("This email is already registered. Redirecting to login...");
        navigate(`/auth?mode=login&email=${encodeURIComponent(values.email)}`);
      } else {
        setAuthError(error.message || "Failed to create account. Please try again later.");
        toast.error("Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const stepFields = {
    1: ["firstName", "lastName", "dateOfBirth"],
    2: ["email", "password", "agreeToTerms"],
  } as const;

  const handleSignupFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupStep < 3) {
      const ok = await signupForm.trigger([...stepFields[signupStep as 1 | 2]]);
      if (!ok) return;
      if (signupStep === 1) {
        const age = calculateAge(signupForm.getValues("dateOfBirth"));
        if (age < 13) {
          setAuthError("You must be at least 13 years old to create an account.");
          return;
        }
      }
      if (signupStep === 2) {
        const passwordCheck = validatePassword(signupForm.getValues("password"));
        if (!passwordCheck.isValid) {
          setPasswordValidationError(passwordCheck.errorMessage);
          return;
        }
      }
      setAuthError(null);
      setPasswordValidationError(null);
      setSignupStep(signupStep + 1);
      return;
    }
    signupForm.handleSubmit(onSignupSubmit)(e);
  };

  // Redirect if user is already logged in (unless they're mid password-reset or just signed up)
  if (user && !suspendRedirect && !signupCompleteEmail) {
    return <Navigate to="/" />;
  }

  // After signup: prompt the user to check their email to finish creating their account
  if (signupCompleteEmail) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="flex-1 container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-anyskill-purple/10">
              <svg className="h-8 w-8 text-anyskill-purple" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 6L2 7" />
              </svg>
            </div>
            <h1 className="font-poppins font-bold text-3xl text-anyskill-purple mb-2">
              Check Your Email
            </h1>
            <p className="text-gray-600 mb-2">
              We sent a confirmation link to <span className="font-medium">{signupCompleteEmail}</span>.
            </p>
            <p className="text-gray-600 mb-8">
              Click the link in that email to finish creating your account. It may take a minute to arrive — don't forget to check your spam folder.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setSignupCompleteEmail(null);
                setSuspendRedirect(false);
                navigate("/auth?mode=login");
              }}
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-poppins font-bold text-3xl text-anyskill-purple mb-2">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-gray-600">
              {isLogin ? "Sign in to continue using AnySkill" : "Join our community and start sharing your skills"}
            </p>
          </div>

          {authError && <Alert variant="destructive" className="mb-6">
              <AlertTitle>Authentication Error</AlertTitle>
              <AlertDescription>{authError}</AlertDescription>
            </Alert>}

          {passwordValidationError && <Alert variant="destructive" className="mb-6">
              <AlertTitle>Password Requirements</AlertTitle>
              <AlertDescription>{passwordValidationError}</AlertDescription>
            </Alert>}

          {!isLogin && <div className="mb-6">

            </div>}

          <Button
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            onClick={onAppleSignIn}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05 1.34-3.13 1.34-1.21 0-2.32-.55-3.45-.55-1.15 0-2.4.57-3.6.57-1.34 0-2.58-.72-3.56-1.73-2.45-2.56-2.1-7.17.73-9.96.96-.94 2.1-1.57 3.36-1.57 1.24 0 2.42.7 3.25.7.8 0 2.08-.74 3.54-.74.67 0 2.3.13 3.5 1.05-.09.06-2.1 1.23-2.1 3.67 0 2.9 2.54 3.88 2.6 3.9-.02.1-.4 1.42-1.2 2.8-.72 1.23-1.47 2.46-2.6 2.46-.69 0-1.16-.46-2.2-.46-1.13 0-1.84.47-2.52.47-.97 0-1.75-.9-2.6-2.02zM12.03 7.25c-.52-1.25.13-2.86 1.34-3.78 1.08-.82 2.5-.95 3.32-.17-.08.82-.55 1.68-1.34 2.27-.94.7-2.2.92-3.32.68z" />
            </svg>
            Continue with Apple
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-2 mt-3"
            onClick={onGoogleSignIn}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">
                or continue with email
              </span>
            </div>
          </div>

          {isLogin ? <Form {...loginForm}>
              <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-6">
                <FormField control={loginForm.control} name="email" render={({
              field
            }) => <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="youremail@example.com" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={loginForm.control} name="password" render={({
              field
            }) => <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="••••••••" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setResetDialogOpen(true);
                      setSuspendRedirect(true);
                    }}
                    className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button type="submit" className="w-full bg-anyskill-purple hover:bg-anyskill-purple/90" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </Form> : <Form {...signupForm}>
              <form onSubmit={handleSignupFormSubmit} className="space-y-6">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Step {signupStep} of 3 — {signupStep === 1 ? "About you" : signupStep === 2 ? "Account details" : "Optional extras"}</span>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((n) => (
                      <span key={n} className={`h-1.5 w-6 rounded-full ${n <= signupStep ? "bg-primary" : "bg-muted"}`} />
                    ))}
                  </div>
                </div>
                {signupStep === 1 && <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={signupForm.control} name="firstName" render={({
                field
              }) => <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="First name" autoComplete="given-name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={signupForm.control} name="lastName" render={({
                field
              }) => <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Last name" autoComplete="family-name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <FormField control={signupForm.control} name="dateOfBirth" render={({
                  field
                }) => (
                  <DateOfBirthField value={field.value} onChange={field.onChange} />
                )} />
                </>}

                {signupStep === 2 && <>

                <FormField control={signupForm.control} name="email" render={({
              field
            }) => <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="youremail@example.com" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={signupForm.control} name="password" render={({
              field
            }) => <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="••••••••" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField 
                  control={signupForm.control} 
                  name="agreeToTerms" 
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/30">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">
                          I have read and agree to the{" "}
                          <Link 
                            to="/privacy-policy" 
                            target="_blank" 
                            className="text-anyskill-blue hover:underline font-medium"
                          >
                            Privacy Policy
                          </Link>
                          {" "}and{" "}
                          <Link 
                            to="/terms-of-service" 
                            target="_blank" 
                            className="text-anyskill-blue hover:underline font-medium"
                          >
                            Terms of Service
                          </Link>
                          .
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
                </>}

                {signupStep === 3 && <>
                <FormField 
                  control={signupForm.control} 
                  name="school" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-muted-foreground">
                        Are you a student? Add your school to connect with others like you.
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="e.g., Scripps Ranch High School (optional)" 
                          autoComplete="organization" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} 
                />

                <FormField 
                  control={signupForm.control} 
                  name="phoneNumber" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        Phone Number
                        <span className="text-xs text-muted-foreground">(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="tel"
                          placeholder="e.g., (555) 123-4567" 
                          autoComplete="tel" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1">
                        Add your phone number to receive important updates about payments and services
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} 
                />

                </>}

                <div className="flex gap-3">
                  {signupStep > 1 && <Button type="button" variant="outline" className="flex-1" onClick={() => setSignupStep(signupStep - 1)}>Back</Button>}
                <Button type="submit" className="flex-1 bg-anyskill-purple hover:bg-anyskill-purple/90" disabled={loading}>
                  {signupStep < 3 ? "Continue" : loading ? "Creating Account..." : "Create Account"}
                </Button>
                </div>
              </form>
            </Form>}

          <div className="mt-6 text-center">
            <Link 
              to={`/auth?mode=${isLogin ? 'signup' : 'login'}`}
              className="text-anyskill-blue hover:underline text-sm font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </Link>
          </div>
        </div>
      </div>

      <PasswordResetDialog
        open={resetDialogOpen}
        onOpenChange={(open) => {
          setResetDialogOpen(open);
          setSuspendRedirect(open);

          if (!open && searchParams.get("reset") === "1") {
            navigate("/auth?mode=login", { replace: true });
          }
        }}
        initialEmail={loginForm.getValues("email")}
        onInProgressChange={setSuspendRedirect}
      />
    </div>
  );
};

export default Auth;
